import { enter, leave } from 'el-transition'
import { Controller } from '@hotwired/stimulus'
import { get, post } from '@rails/request.js'

export default class extends Controller {
  static targets = [
    'countdownClock',
    'loginError',
    'loginModal',
    'notificationContainer',
    'passwordInput',
    'refreshErrorMessage',
  ]

  static values = {
    countdownExpirationSeconds: { default: 120, type: Number },
    genericError: {
      default: 'There was an unexpected error. Please try again.',
      type: String,
    },
    initialLoginExpiresAt: String,
    refreshCsrfUrl: String,
  }

  connect() {
    this.loginExpiresAt = new Date(this.initialLoginExpiresAtValue)
    this.attemptCsrfRefresh = true
    this.#pollTimeout()
  }

  async handleLogin(event) {
    event.preventDefault()
    this.loginErrorTarget.innerHTML = ''
    let fetchResult

    try {
      fetchResult = await post(
        event.target.action,
        {
          body: new FormData(event.target),
          responseKind: 'json',
        },
      )
    } catch (error) {
      this.loginErrorTarget.innerHTML = this.genericErrorValue
    }

    const response = await fetchResult.response
    const data = await fetchResult.json

    if (response.ok) {
      this.statusValue = 'success'
      leave(this.loginModalTarget)
      this.passwordInputTargets.forEach((input) => { input.value = '' })
      this.loginExpiresAt = new Date(data.expires_at)
      this.#updateAuthenticityToken(data.authenticity_token)
      this.#pollTimeout()
      this.attemptCsrfRefresh = true
    } else if (response.status === 401) {
      this.loginErrorTarget.innerHTML = data.error
      this.attemptCsrfRefresh = true
    } else if (response.status === 404 && this.attemptCsrfRefresh === true) {
      this.#refreshCsrfAndRetry(event)
    } else {
      this.loginErrorTarget.innerHTML = this.genericErrorValue
    }

    event.target.querySelector('input[type="submit"]').disabled = false
  }

  async handleRefresh(event) {
    event.preventDefault()
    leave(this.refreshErrorMessageTarget)

    let response

    try {
      response = await get(event.target.href, { responseKind: 'json' })
    } catch (error) {
      enter(this.refreshErrorMessageTarget)
    }

    if (response.ok) {
      const data = await response.json
      this.loginExpiresAt = new Date(data.expires_at)
      leave(this.notificationContainerTarget)
    } else {
      enter(this.refreshErrorMessageTarget)
    }

    clearInterval(this.notificationInterval)
    this.#pollTimeout()
  }

  async #refreshCsrfAndRetry(event) {
    this.attemptCsrfRefresh = false
    let response

    try {
      response = await get(this.refreshCsrfUrlValue, { responseKind: 'json' })
    } catch (error) {
      this.loginErrorTarget.innerHTML = this.genericErrorValue
    }

    if (response.ok) {
      const data = await response.json
      this.#updateAuthenticityToken(data.authenticity_token)
      this.handleLogin(event)
    }
  }

  #pollTimeout() {
    this.pollingInterval = setInterval(
      () => { this.#checkUserTimeout() },
      1000,
    )
  }

  #checkUserTimeout() {
    const currentTime = Date.now()
    if (currentTime > this.loginExpiresAt) {
      enter(this.loginModalTarget)
      clearInterval(this.pollingInterval)
    } else if (this.loginExpiresAt - currentTime < this.countdownExpirationSecondsValue * 1000) {
      this.#startNotificationCountdown()
      clearInterval(this.pollingInterval)
    }
  }

  #startNotificationCountdown() {
    leave(this.refreshErrorMessageTarget)
    this.notificationInterval = setInterval(
      () => { this.#updateCountdownClock() },
      (1000 / 4),
    )
    enter(this.notificationContainerTarget)
  }

  #updateAuthenticityToken(authenticityToken) {
    document.
      querySelector('meta[name="csrf-token"]').
      setAttribute('content', authenticityToken)

    document.
      querySelectorAll('input[name="authenticity_token"]').
      forEach((input) => { input.value = authenticityToken })
  }

  #updateCountdownClock() {
    const millisecondsLeft = this.loginExpiresAt - Date.now()

    if (millisecondsLeft >= 0) {
      const minutesLeft = Math.floor(millisecondsLeft / (1000 * 60))
      const secondsLeft = Math.floor((millisecondsLeft % (1000 * 60)) / 1000)
      const secondLabel = (secondsLeft === 1) ? 'second' : 'seconds'

      if (minutesLeft > 0) {
        const minuteLabel = (minutesLeft > 1) ? 'minutes' : 'minute'
        this.countdownClockTarget.innerHTML =
          `${minutesLeft} ${minuteLabel} ${secondsLeft} ${secondLabel}`
      } else {
        this.countdownClockTarget.innerHTML = `${secondsLeft} ${secondLabel}`
      }
    } else {
      leave(this.notificationContainerTarget)
      enter(this.loginModalTarget)
      clearInterval(this.notificationInterval)
    }
  }
}
